import React, { FC, useMemo, useState, useEffect } from 'react';
import './SecondaryTrainingGoalStep.scss';
import { useGetProgramMasterData } from 'src/api/mutations/useGetProgramMasterData';
import { ReactComponent as Increase_strengthIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/increase_strength.svg';
import { ReactComponent as Injury_preventionIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/injury_prevention.svg';
import { ReactComponent as General_fitnessIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/general_fitness.svg';
import { ReactComponent as RehabilitationIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/rehabilitation.svg';
import { ReactComponent as Build_muscleIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/build_muscle.svg';
import { ReactComponent as Weight_lossIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/weight_loss.svg';
import { ReactComponent as PerformanceIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/performance.svg';
import { ReactComponent as EnduranceIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/endurance.svg';
import { ReactComponent as MobilityIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/mobility.svg';

interface SecondaryTrainingGoalStepProps {
  value?: string[];
  primaryGoal?: string;
  onChange?: (goals: string[]) => void;
}

const iconMapping: Record<string, any> = {
  increase_strength: <Increase_strengthIcon />,
  build_muscle: <Build_muscleIcon />,
  weight_loss: <Weight_lossIcon />,
  endurance: <EnduranceIcon />,
  mobility: <MobilityIcon />,
  performance: <PerformanceIcon />,
  general_fitness: <General_fitnessIcon />,
  rehabilitation: <RehabilitationIcon />,
  injury_prevention: <Injury_preventionIcon />,
};

// What else should the program support?
export const SecondaryTrainingGoalStep: FC<SecondaryTrainingGoalStepProps> = ({
  value,
  primaryGoal,
  onChange,
}) => {
  const [selectedGoals, setSelectedGoals] = useState<string[]>(() => {
    const initial = value || [];
    return initial.filter((g) => g !== primaryGoal);
  });

  const { data: programMasterData, isLoading, isError } = useGetProgramMasterData();
  const secondaryTrainingGoals = programMasterData?.secondaryTrainingGoals;

  const options = useMemo(() => {
    const secOptions = secondaryTrainingGoals?.options || [];
    const primOptions = programMasterData?.trainingGoal?.options || [];

    // Ensure all primary goals are available as secondary goals if not explicitly excluded
    const merged = [...secOptions];
    primOptions.forEach((opt) => {
      if (!merged.find((o) => o.slug === opt.slug)) {
        merged.push(opt);
      }
    });
    return merged;
  }, [secondaryTrainingGoals?.options, programMasterData?.trainingGoal?.options]);
  const maxSelection = secondaryTrainingGoals?.maxSelection || 2;

  useEffect(() => {
    if (primaryGoal && selectedGoals.includes(primaryGoal)) {
      const filtered = selectedGoals.filter((id) => id !== primaryGoal);
      setSelectedGoals(filtered);
      if (onChange) {
        onChange(filtered);
      }
    }
  }, [primaryGoal, selectedGoals, onChange]);

  const goalOptions = useMemo(() => {
    if (!options) return [];
    return [...options]
      .filter((opt) => opt.slug !== primaryGoal)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [options, primaryGoal]);

  const toggleGoal = (goalId: string) => {
    let newGoals: string[];
    if (selectedGoals.includes(goalId)) {
      newGoals = selectedGoals.filter((id) => id !== goalId);
    } else {
      if (selectedGoals.length < maxSelection) {
        newGoals = [...selectedGoals, goalId];
      } else {
        newGoals = selectedGoals;
      }
    }
    setSelectedGoals(newGoals);
    if (onChange) {
      onChange(newGoals);
    }
  };

  if (isLoading) {
    return <div className="secondary-training-goal-step">{'Loading options...'}</div>;
  }

  if (isError || !secondaryTrainingGoals) {
    return <div className="secondary-training-goal-step">{'Error loading options.'}</div>;
  }

  return (
    <div className="secondary-training-goal-step">
      <h2 className="step-title">{secondaryTrainingGoals.title}</h2>
      {secondaryTrainingGoals.subtitle && (
        <p className="step-subtitle">{secondaryTrainingGoals.subtitle}</p>
      )}

      <div className="goal-buttons">
        {goalOptions.map((goal) => {
          const isSelected = selectedGoals.includes(goal.slug);
          const isDisabled = !isSelected && selectedGoals.length >= maxSelection;

          return (
            <button
              key={goal.id}
              className={`goal-btn ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                if (!isDisabled || isSelected) {
                  toggleGoal(goal.slug);
                }
              }}
              style={{
                opacity: isDisabled ? 0.5 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              <div className="goal-icon">{iconMapping[goal.id]}</div>
              <span className="goal-label">{goal.title}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
